/**
 * Shared body-scroll lock for full-screen modal components (Popup,
 * AnnouncementList).
 *
 * A reference counter — not a boolean — is deliberate: while the user moves
 * from the list into a popup and back, two modals overlap for an instant
 * (the incoming one is shown before the outgoing one is hidden). A boolean
 * lock would unlock on the first hide and momentarily restore the page's
 * overflow, making the host page visibly jump. With a counter the page stays
 * locked as long as at least one modal holds it, and the ORIGINAL overflow is
 * only restored when the last one lets go.
 */

let lockCount = 0;

// The host page's overflow captured at the moment the first lock is taken, so
// a page that intentionally used e.g. `overflow: scroll` is restored verbatim.
let originalBodyOverflow = '';

/**
 * Acquire a body-scroll lock. Safe to call while another modal already holds
 * one — the page is locked once and the count is incremented.
 */
export function lockBodyScroll(): void {
    if (lockCount === 0) {
        originalBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
    }

    lockCount += 1;
}

/**
 * Release a previously acquired lock. The page's original overflow is restored
 * only when the final holder releases. Calling this more times than
 * `lockBodyScroll()` is a no-op, so a double-hide can never leave the page
 * stuck locked (nor drive the counter negative).
 */
export function unlockBodyScroll(): void {
    if (lockCount === 0) {
        return;
    }

    lockCount -= 1;

    if (lockCount === 0) {
        document.body.style.overflow = originalBodyOverflow;
    }
}

/**
 * Force the lock back to its released state and restore the page's overflow.
 * Intended for teardown paths (and test isolation) where any outstanding locks
 * must be dropped unconditionally.
 */
export function resetBodyScrollLock(): void {
    if (lockCount > 0) {
        document.body.style.overflow = originalBodyOverflow;
    }

    lockCount = 0;
    originalBodyOverflow = '';
}
